const SYSTEM_COLUMN_KEYS = ['rownum', 'index', 'oper', 'oper0', 'expand']
const REQUIRED_SCOPE_KEYS = ['settingFormId', 'fieldIdseq', 'fieldLstseq', 'userNo', 'corpId']

function stringValue(value) {
    if (value === undefined || value === null) return ''
    return String(value)
}

function keyIdentity(value) {
    return stringValue(value).trim().toUpperCase()
}

function readValue(row, keys) {
    if (!row) return undefined
    for (let index = 0; index < keys.length; index += 1) {
        const value = row[keys[index]]
        if (value !== undefined && value !== null && value !== '') return value
    }
    return undefined
}

function positiveNumber(value, fallback = 0) {
    const numberValue = Number(value)
    return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : fallback
}

function sortValue(value, fallback) {
    const numberValue = Number(value)
    return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : fallback
}

function isVisible(row) {
    return keyIdentity(readValue(row, ['VISFLAG', 'visflag'])) !== 'N'
}

function isExpansionRow(row) {
    const type = stringValue(readValue(row, ['type', 'TYPE', 'columnType', 'COLUMNTYPE'])).toLowerCase()
    return type === 'expand' || row && (row.expand === true || row.EXPAND === true)
}

function isPersistentRow(row, fixedFieldKeys = []) {
    const key = getFieldKey(row)
    const fixedKeys = new Set((fixedFieldKeys || []).map(keyIdentity))
    return !!key
        && !SYSTEM_COLUMN_KEYS.includes(keyIdentity(key).toLowerCase())
        && !fixedKeys.has(keyIdentity(key))
        && !isExpansionRow(row)
}

function isConfigurableMetadata(field, options = {}) {
    const hideFlag = keyIdentity(readValue(field, ['THIDE', 'thide']))
    return hideFlag === 'N' && isPersistentRow(field, options.fixedFieldKeys)
}

function assertCompleteScope(scope) {
    const normalized = normalizeScope(scope)
    const missing = REQUIRED_SCOPE_KEYS.filter(key => !normalized[key])
    if (missing.length) {
        const error = new Error('Field settings scope is missing: ' + missing.join(', '))
        error.name = 'FormFieldSettingsScopeError'
        error.missing = missing
        throw error
    }
    return normalized
}

function rowsByLogicalIdentity(rows, scope, refItmseqReader = metadataRefItmseq) {
    const groups = {}
    const fieldRows = rows || []
    fieldRows.forEach(row => {
        const identity = logicalRowIdentity(row, refItmseqReader)
        if (!identity) return
        if (!groups[identity]) groups[identity] = []
        groups[identity].push(row)
    })

    const map = {}
    const conflicts = []
    Object.keys(groups).forEach(identity => {
        if (groups[identity].length === 1) {
            map[identity] = groups[identity][0]
            return
        }
        conflicts.push({
            type: 'duplicate-logical-row',
            key: getFieldKey(groups[identity][0]),
            refItmseq: refItmseqReader(groups[identity][0]),
            scope: normalizeScope(scope),
            rows: groups[identity].map(row => Object.assign({}, row)),
        })
    })
    return { map, conflicts }
}

function physicalRowsByLogicalIdentity(rows, scope) {
    const result = {}
    getScopedStoredRows(rows, scope).forEach(row => {
        const identity = logicalRowIdentity(row, storedRefItmseq)
        const listSeq = physicalListSeq(row)
        if (!identity || !listSeq) return
        if (!result[identity]) result[identity] = new Set()
        result[identity].add(listSeq)
    })
    return result
}

function allowedListSeq(row, allowedRows) {
    const listSeq = physicalListSeq(row)
    const identity = logicalRowIdentity(row, metadataRefItmseq)
    const physicalListSeqs = allowedRows && allowedRows[identity]
    if (!identity || !physicalListSeqs) return ''
    if (physicalListSeqs.size === 1) return Array.from(physicalListSeqs)[0]
    if (listSeq && physicalListSeqs.has(listSeq)) return listSeq
    return ''
}

function applyPhysicalListSeq(rows, storedRows, scope) {
    const allowedRows = physicalRowsByLogicalIdentity(storedRows, scope)
    return (rows || []).map(row => {
        const listSeq = allowedListSeq(row, allowedRows)
        return listSeq ? Object.assign({}, row, { LSTSEQ: listSeq }) : Object.assign({}, row)
    })
}

function createDefaultRow(field, scope, index, options) {
    const key = getFieldKey(field)
    const displayName = options && typeof options.getDisplayName === 'function'
        ? options.getDisplayName(field)
        : readValue(field, ['DISPNM', 'FLDNM', 'CNAME', 'title'])
    return {
        IDSEQ: scope.settingFormId,
        LSTSEQ: '',
        CORPID: scope.corpId,
        USERNO: scope.userNo,
        CODE: key,
        CNAME: stringValue(readValue(field, ['FLDNM', 'CNAME', 'title']) || key),
        DISPNM: stringValue(displayName || key),
        REFIDSEQ: scope.fieldIdseq,
        REFLSTSEQ: scope.fieldLstseq,
        REFITMSEQ: metadataRefItmseq(field),
        VISFLAG: 'Y',
        WIDTH: positiveNumber(readValue(field, ['WIDTH', 'width']), 0),
        SORTBY: sortValue(readValue(field, ['SORTBY', 'sortby']), index + 1),
    }
}

function applyStoredRow(defaultRow, storedRow, widthMap) {
    const runtimeWidth = positiveNumber(
        widthMap && (widthMap[keyIdentity(defaultRow.CODE)] || widthMap[defaultRow.CODE]),
        0
    )
    if (!storedRow) return Object.assign({}, defaultRow, { WIDTH: runtimeWidth || defaultRow.WIDTH })
    return Object.assign({}, defaultRow, {
        LSTSEQ: physicalListSeq(storedRow),
        VISFLAG: keyIdentity(readValue(storedRow, ['VISFLAG', 'visflag'])) === 'N' ? 'N' : 'Y',
        WIDTH: runtimeWidth || positiveNumber(readValue(storedRow, ['WIDTH', 'width']), defaultRow.WIDTH),
        SORTBY: sortValue(readValue(storedRow, ['SORTBY', 'sortby']), defaultRow.SORTBY),
    })
}

function responseBody(response) {
    if (response && response.data && typeof response.data === 'object'
        && (response.status !== undefined || response.config || response.data.code !== undefined)) {
        return response.data
    }
    return response
}

function responseError(response, fallback) {
    const body = responseBody(response)
    const message = body && (body.message || body.error || (typeof body.result === 'string' ? body.result : ''))
    const error = new Error(message || fallback)
    error.response = response
    return error
}

function assertSuccessfulResponse(response, fallback) {
    const body = responseBody(response)
    if (!body || String(body.code) !== '200') throw responseError(response, fallback)
    return response
}

function conflictError(conflicts) {
    const error = new Error('字段设置存在重复配置，请先清理重复记录后再保存。')
    error.name = 'FormFieldSettingsConflictError'
    error.conflicts = conflicts
    return error
}

function persistenceError(message, mismatches = [], cause) {
    const error = new Error(message || '字段设置保存后校验失败。')
    error.name = 'FormFieldSettingsPersistenceError'
    error.mismatches = mismatches
    if (cause) error.cause = cause
    return error
}

function persistedVisibility(row) {
    const value = keyIdentity(readValue(row, ['VISFLAG', 'visflag']))
    return value === 'Y' || value === 'N' ? value : ''
}

function persistedRefItmseq(row) {
    return storedRefItmseq(row)
}

function metadataRefItmseq(row) {
    return stringValue(readValue(row, ['ITMSEQ', 'itmseq', 'REFITMSEQ', 'refitmseq']))
}

function storedRefItmseq(row) {
    return stringValue(readValue(row, ['REFITMSEQ', 'refitmseq']))
}

function physicalListSeq(row) {
    return stringValue(readValue(row, ['LSTSEQ', 'lstseq'])).trim()
}

function logicalRowIdentity(row, refItmseqReader = metadataRefItmseq) {
    const key = keyIdentity(getFieldKey(row))
    const refItmseq = refItmseqReader(row)
    return key && refItmseq ? key + '\u0000' + refItmseq : ''
}

export function getFieldKey(item) {
    return stringValue(readValue(item, ['FLDNO', 'fldno', 'CODE', 'code', 'key', 'KEY', 'field', 'FIELD'])).trim()
}

export function normalizeScope(scope = {}) {
    return {
        settingFormId: stringValue(scope.settingFormId || scope.formId || scope.IDSEQ),
        fieldIdseq: stringValue(scope.fieldIdseq || scope.refIdseq || scope.REFIDSEQ),
        fieldLstseq: stringValue(scope.fieldLstseq || scope.refLstseq || scope.REFLSTSEQ),
        userNo: stringValue(scope.userNo || scope.userno || scope.USERNO),
        corpId: stringValue(scope.corpId || scope.corpid || scope.CORPID),
    }
}

export function getScopedStoredRows(rows, scope) {
    const normalized = assertCompleteScope(scope)
    return (rows || []).filter(row => (
        stringValue(readValue(row, ['CORPID', 'corpid'])) === normalized.corpId
        && stringValue(readValue(row, ['USERNO', 'userno'])) === normalized.userNo
        && stringValue(readValue(row, ['IDSEQ', 'idseq'])) === normalized.settingFormId
        && stringValue(readValue(row, ['REFIDSEQ', 'refidseq'])) === normalized.fieldIdseq
        && stringValue(readValue(row, ['REFLSTSEQ', 'reflstseq'])) === normalized.fieldLstseq
    )).map(row => Object.assign({}, row))
}

function getConfigurableStoredRows(rows, scope, fixedFieldKeys) {
    // Keep the full scoped set for physical-row reuse, but only configurable
    // fields participate in duplicate detection and merge overrides.
    return getScopedStoredRows(rows, scope).filter(row => isPersistentRow(row, fixedFieldKeys))
}

export function normalizeSortBy(rows) {
    const sourceRows = rows || []
    const preserveDraftOrder = sourceRows.length > 0
        && sourceRows.every(row => typeof row.__fieldSettingSelected === 'boolean')
    const indexedRows = sourceRows.map((row, index) => Object.assign({ __fieldSettingsIndex: index }, row))
    if (!preserveDraftOrder) {
        indexedRows.sort((left, right) => {
            const leftSort = sortValue(readValue(left, ['SORTBY', 'sortby']), 1000000 + left.__fieldSettingsIndex)
            const rightSort = sortValue(readValue(right, ['SORTBY', 'sortby']), 1000000 + right.__fieldSettingsIndex)
            if (leftSort !== rightSort) return leftSort - rightSort
            return left.__fieldSettingsIndex - right.__fieldSettingsIndex
        })
    }
    return indexedRows.map((row, index) => {
        const normalized = Object.assign({}, row, { SORTBY: index + 1 })
        delete normalized.__fieldSettingsIndex
        return normalized
    })
}

export function sanitizeFieldSettings(rows, lockedFieldKeys = []) {
    const locked = new Set((lockedFieldKeys || []).map(keyIdentity))
    return (rows || []).map(row => {
        const next = Object.assign({}, row)
        next.VISFLAG = locked.has(keyIdentity(getFieldKey(next))) || isVisible(next) ? 'Y' : 'N'
        return next
    })
}

export function mergeFieldSettings(metadataRows, storedRows, scope, options = {}) {
    const normalizedScope = assertCompleteScope(scope)
    const scopedRows = getScopedStoredRows(storedRows, normalizedScope)
    const grouped = rowsByLogicalIdentity(
        getConfigurableStoredRows(storedRows, normalizedScope, options.fixedFieldKeys),
        normalizedScope,
        storedRefItmseq
    )
    const rows = (metadataRows || []).filter(field => isConfigurableMetadata(field, options)).map((field, index) => {
        const defaultRow = createDefaultRow(field, normalizedScope, index, options)
        return applyStoredRow(
            defaultRow,
            grouped.map[logicalRowIdentity(defaultRow, metadataRefItmseq)],
            options.widthMap || {}
        )
    })
    return {
        rows: normalizeSortBy(sanitizeFieldSettings(rows, options.lockedFieldKeys)),
        storedRows: scopedRows,
        conflicts: grouped.conflicts,
    }
}

export function resetFieldSettings(metadataRows, storedRows, scope, options = {}) {
    const normalizedScope = assertCompleteScope(scope)
    const scopedRows = getScopedStoredRows(storedRows, normalizedScope)
    const grouped = rowsByLogicalIdentity(
        getConfigurableStoredRows(storedRows, normalizedScope, options.fixedFieldKeys),
        normalizedScope,
        storedRefItmseq
    )
    const rows = (metadataRows || []).filter(field => isConfigurableMetadata(field, options)).map((field, index) => {
        const row = createDefaultRow(field, normalizedScope, index, options)
        const storedRow = grouped.map[logicalRowIdentity(row, metadataRefItmseq)]
        if (storedRow) {
            row.LSTSEQ = physicalListSeq(storedRow)
        }
        return row
    })
    return {
        rows: normalizeSortBy(sanitizeFieldSettings(rows, options.lockedFieldKeys)),
        storedRows: scopedRows,
        conflicts: grouped.conflicts,
    }
}

export function buildFieldSettingsPayload(rows, scope, options = {}) {
    const normalizedScope = assertCompleteScope(scope)
    const persistentRows = (rows || []).filter(row => isPersistentRow(row, options.fixedFieldKeys))
    const missingRefRows = persistentRows.filter(row => !metadataRefItmseq(row))
    if (missingRefRows.length) {
        const error = persistenceError('字段设置缺少字段元数据 ITMSEQ，未提交保存。', missingRefRows.map(row => ({
            type: 'missing-metadata-refitmseq',
            key: getFieldKey(row),
            field: 'REFITMSEQ',
        })))
        error.scope = normalizedScope
        throw error
    }
    const grouped = rowsByLogicalIdentity(persistentRows, normalizedScope, metadataRefItmseq)
    const allowedRows = physicalRowsByLogicalIdentity(options.allowedStoredRows || options.storedRows || [], normalizedScope)
    const normalizedRows = normalizeSortBy(sanitizeFieldSettings(persistentRows, options.lockedFieldKeys))
    return {
        rows: normalizedRows.map(row => ({
            IDSEQ: normalizedScope.settingFormId,
            LSTSEQ: allowedListSeq(row, allowedRows),
            CORPID: normalizedScope.corpId,
            USERNO: normalizedScope.userNo,
            CODE: getFieldKey(row),
            CNAME: stringValue(readValue(row, ['CNAME', 'cname', 'FLDNM', 'fldnm']) || getFieldKey(row)),
            REFIDSEQ: normalizedScope.fieldIdseq,
            REFLSTSEQ: normalizedScope.fieldLstseq,
            REFITMSEQ: metadataRefItmseq(row),
            VISFLAG: keyIdentity(readValue(row, ['VISFLAG', 'visflag'])) === 'N' ? 'N' : 'Y',
            WIDTH: positiveNumber(readValue(row, ['WIDTH', 'width']), 0),
            SORTBY: sortValue(readValue(row, ['SORTBY', 'sortby']), 0),
        })),
        conflicts: grouped.conflicts,
    }
}

export function assertPersistedFieldSettings(expectedRows, storedRows, scope) {
    const normalizedScope = assertCompleteScope(scope)
    const scopedRows = getScopedStoredRows(storedRows, normalizedScope)
    const rowsToCheck = expectedRows || []
    const expectedIdentities = new Set(rowsToCheck.map(row => (
        logicalRowIdentity(row, metadataRefItmseq)
    )).filter(Boolean))
    const relevantStoredRows = scopedRows.filter(row => (
        expectedIdentities.has(logicalRowIdentity(row, storedRefItmseq))
    ))
    const grouped = rowsByLogicalIdentity(relevantStoredRows, normalizedScope, storedRefItmseq)
    const mismatches = grouped.conflicts.map(conflict => ({
        type: 'duplicate-row',
        key: conflict.key,
        refItmseq: conflict.refItmseq,
        count: conflict.rows.length,
    }))

    rowsToCheck.forEach(expectedRow => {
        const key = getFieldKey(expectedRow)
        const identity = logicalRowIdentity(expectedRow, metadataRefItmseq)
        const actualRow = grouped.map[identity]
        const expectedRefItmseq = persistedRefItmseq(expectedRow)
        if (!expectedRefItmseq) {
            mismatches.push({
                type: 'missing-metadata-refitmseq',
                key,
                field: 'REFITMSEQ',
                expected: '字段元数据 ITMSEQ',
                actual: '',
            })
            return
        }
        if (!actualRow) {
            mismatches.push({ type: 'missing-row', key, refItmseq: expectedRefItmseq })
            return
        }

        const expectedListSeq = physicalListSeq(expectedRow)
        const actualListSeq = physicalListSeq(actualRow)
        if (!actualListSeq) {
            mismatches.push({
                type: 'missing-physical-lstseq',
                key,
                refItmseq: expectedRefItmseq,
                field: 'LSTSEQ',
            })
        } else if (expectedListSeq && expectedListSeq !== actualListSeq) {
            mismatches.push({
                type: 'mismatched-value',
                key,
                field: 'LSTSEQ',
                expected: expectedListSeq,
                actual: actualListSeq,
            })
        }

        const comparisons = [
            ['CODE', keyIdentity(key), keyIdentity(getFieldKey(actualRow))],
            ['REFITMSEQ', expectedRefItmseq, persistedRefItmseq(actualRow)],
            ['VISFLAG', persistedVisibility(expectedRow), persistedVisibility(actualRow)],
            ['SORTBY', sortValue(readValue(expectedRow, ['SORTBY', 'sortby']), 0), sortValue(readValue(actualRow, ['SORTBY', 'sortby']), 0)],
            ['WIDTH', positiveNumber(readValue(expectedRow, ['WIDTH', 'width']), 0), positiveNumber(readValue(actualRow, ['WIDTH', 'width']), 0)],
        ]
        comparisons.forEach(([field, expected, actual]) => {
            if (expected === actual) return
            mismatches.push({ type: 'mismatched-value', key, field, expected, actual })
        })
    })

    if (mismatches.length) {
        const error = persistenceError('字段设置保存后与数据库配置不一致，请重试。', mismatches)
        error.scope = normalizedScope
        throw error
    }

    return {
        rows: applyPhysicalListSeq(expectedRows, scopedRows, normalizedScope),
        storedRows: scopedRows,
        conflicts: [],
    }
}

export function createFormFieldSettingsService(dependencies = {}) {
    const fetchTableRows = dependencies.getTabColOrData
    const request = dependencies.request

    function loadStoredRows(scope, context = {}) {
        if (typeof fetchTableRows !== 'function') {
            return Promise.reject(new Error('getTabColOrData is required to load field settings.'))
        }
        let normalizedScope
        try {
            normalizedScope = assertCompleteScope(scope)
        } catch (error) {
            return Promise.reject(error)
        }
        return fetchTableRows(
            context.url,
            'V_FORM_FLDSET',
            'idseq', normalizedScope.settingFormId,
            'userno', normalizedScope.userNo,
            'corpid', normalizedScope.corpId,
            ' order by sortby,lstseq'
        ).then(response => {
            assertSuccessfulResponse(response, '字段设置加载失败。')
            const body = responseBody(response)
            const allRows = Array.isArray(body && body.result) ? body.result : []
            const rows = getScopedStoredRows(allRows, normalizedScope)
            return {
                rows,
                allRows,
                conflicts: rowsByLogicalIdentity(rows, normalizedScope, storedRefItmseq).conflicts,
            }
        })
    }

    function load(scope, context = {}, metadataRows = [], options = {}) {
        return loadStoredRows(scope, context).then(result => {
            const merged = mergeFieldSettings(metadataRows, result.rows, scope, options)
            return {
                rows: merged.rows,
                storedRows: result.rows,
                allRows: result.allRows,
                conflicts: merged.conflicts,
            }
        })
    }

    function save(scope, rows, context = {}, options = {}) {
        if (typeof request !== 'function') {
            return Promise.reject(new Error('request is required to save field settings.'))
        }
        let payload
        try {
            payload = buildFieldSettingsPayload(rows, scope, Object.assign({}, options, {
                allowedStoredRows: options.allowedStoredRows || options.storedRows || [],
            }))
        } catch (error) {
            return Promise.reject(error)
        }
        const storedRows = options.allowedStoredRows || options.storedRows || []
        const storedConflicts = rowsByLogicalIdentity(
            getConfigurableStoredRows(storedRows, scope, options.fixedFieldKeys),
            scope,
            storedRefItmseq
        ).conflicts
        const conflicts = (options.conflicts || []).concat(storedConflicts, payload.conflicts || [])
        if (conflicts.length) return Promise.reject(conflictError(conflicts))

        return request({
            method: 'post',
            url: 'xuper_auth/batDtlSave2',
            data: {
                p_json: JSON.stringify(payload.rows),
                p_table: 'form_fldset',
                p_idseq: normalizeScope(scope).settingFormId,
                p_action: 'S',
            },
        }).then(response => {
            assertSuccessfulResponse(response, '字段设置保存失败。')
            if (typeof fetchTableRows !== 'function' || !context.url) {
                throw persistenceError('字段设置已提交，但缺少保存后回读校验配置。', [{
                    type: 'readback-unavailable',
                }])
            }
            return loadStoredRows(scope, context).then(refreshed => {
                const verified = assertPersistedFieldSettings(payload.rows, refreshed.rows, scope)
                return {
                    rows: verified.rows,
                    storedRows: verified.storedRows,
                    allRows: refreshed.allRows,
                    conflicts: verified.conflicts,
                    response,
                }
            }).catch(error => {
                if (error && error.name === 'FormFieldSettingsPersistenceError') throw error
                throw persistenceError('字段设置已提交，但保存后回读校验失败。', [{
                    type: 'readback-request-failed',
                    message: error && error.message ? error.message : '',
                }], error)
            })
        })
    }

    return { loadStoredRows, load, save }
}

export default createFormFieldSettingsService
